import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { type User, useAuthStore } from "../../lib/auth-store";
import {
  MagliaEditor,
  MAGLIA_DEFAULT,
  magliaPayload,
  type MagliaState,
} from "../common/MagliaEditor";
import { tr } from "../../i18n";

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface RegistrazioneResponse extends TokenResponse {
  lega_id: string | null;
  lega_nome: string | null;
  squadra_id: string | null;
  squadra_nome: string | null;
}

interface AnteprimaInvito {
  tipo_invito: string;
  ruolo_lega: string;
  email_destinatario: string;
  nome_squadra_destinazione: string | null;
  /** l'email ha gia' un account: si accetta da loggati (Andrea 2026-10-08) */
  email_gia_registrata?: boolean;
}

interface RegistrazioneInfo {
  con_codice_lega: boolean;
  libera: boolean;
  crea_lega?: boolean;
}

/** Come ci si registra (Andrea 2026-10-06): fondare la propria lega (account
 *  ora, lega subito dopo da /leghe), entrare con il codice di una lega
 *  (XXXX-XXXX, dal presidente) o con un invito via email (link o codice
 *  XXX-YYY-ZZZ). */
type Modo = "crea" | "lega" | "invito";

const SCELTE: { modo: Modo; icona: string; titolo: string; testo: string }[] = [
  {
    modo: "crea",
    icona: "🏆",
    titolo: tr("Crea una lega"),
    testo: tr("Fondi la tua lega e invita gli amici: ne sarai il presidente."),
  },
  {
    modo: "lega",
    icona: "🔑",
    titolo: tr("Unisciti con un codice"),
    testo: tr("Hai il codice di una lega (es. ABCD-EFGH)? Entri subito con una squadra."),
  },
  {
    modo: "invito",
    icona: "✉️",
    titolo: tr("Ho un invito via email"),
    testo: tr("Hai ricevuto un link o un codice d'invito (es. ABC-123-XYZ)."),
  },
];

const INPUT =
  "w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg focus:outline-none focus:ring-2 focus:ring-accent";
const LABEL = "block text-xs uppercase tracking-wider text-fg-muted mb-1";

function messaggioErrore(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const primo = detail[0] as { msg?: string };
    if (primo?.msg) return primo.msg;
  }
  return fallback;
}

export function RegisterPage() {
  const [params] = useSearchParams();
  const urlToken = params.get("token") ?? "";
  const codiceLegaUrl = params.get("lega") ?? "";
  const { setTokens, setUser, isAuthenticated } = useAuthStore();

  const [info, setInfo] = useState<RegistrazioneInfo | null>(null);
  const [modo, setModo] = useState<Modo | null>(
    urlToken ? "invito" : codiceLegaUrl ? "lega" : null,
  );
  const [codice, setCodice] = useState("");
  const [codiceLega, setCodiceLega] = useState(codiceLegaUrl.toUpperCase());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nome, setNome] = useState("");
  const [nomeSquadra, setNomeSquadra] = useState("");
  const [maglia, setMaglia] = useState<MagliaState>(MAGLIA_DEFAULT);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<null | { to: string }>(null);
  const [loading, setLoading] = useState(false);
  const [anteprima, setAnteprima] = useState<AnteprimaInvito | null>(null);

  useEffect(() => {
    if (urlToken) setCodice(""); // se ho token dal link, codice manuale non serve
  }, [urlToken]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<RegistrazioneInfo>("/auth/registrazione")
      .then((r) => {
        if (!cancelled) setInfo(r.data);
      })
      .catch(() => {
        if (!cancelled) setInfo({ con_codice_lega: false, libera: false, crea_lega: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Andrea 2026-09-01: chiedi al backend che tipo di invito è, così il form
  // per l'allenatore in seconda NON mostra gli step "crea la tua squadra".
  useEffect(() => {
    const codicePronto = codice.trim().length === 11;
    if (modo !== "invito" || (!urlToken && !codicePronto)) {
      setAnteprima(null);
      return;
    }
    const qs = urlToken
      ? `token=${encodeURIComponent(urlToken)}`
      : `codice=${encodeURIComponent(codice.trim().toUpperCase())}`;
    let cancelled = false;
    api
      .get<AnteprimaInvito>(`/auth/inviti/anteprima?${qs}`)
      .then((r) => {
        if (!cancelled) setAnteprima(r.data);
      })
      .catch(() => {
        if (!cancelled) setAnteprima(null);
      });
    return () => {
      cancelled = true;
    };
  }, [urlToken, codice, modo]);

  const isSecondo = modo === "invito" && anteprima?.tipo_invito === "allenatore_secondo";
  // Chi crea una lega sceglie squadra e maglia nel passo dopo, con la lega.
  const conSquadra = modo !== "crea" && !isSecondo;

  // Andrea 2026-10-08: chi e' gia' loggato e apre un link d'invito lo
  // accetta col suo account (cessione squadra, allenatore in seconda).
  const urlAccetta = urlToken
    ? `/accetta-invito?token=${encodeURIComponent(urlToken)}`
    : codice.trim()
      ? `/accetta-invito?codice=${encodeURIComponent(codice.trim().toUpperCase())}`
      : null;
  if (isAuthenticated() && !success) return <Navigate to={urlAccetta ?? "/"} replace />;
  if (success) return <Navigate to={success.to} replace />;
  if (modo === "invito" && anteprima?.email_gia_registrata && urlAccetta) {
    const destinazione = { pathname: "/accetta-invito", search: urlAccetta.split("?")[1] ?? "" };
    return (
      <div className="min-h-screen bg-bg text-fg flex items-center justify-center p-6">
        <div className="card max-w-md w-full space-y-3 text-center">
          <div className="font-display text-xl text-accent">{tr("Hai già un account")}</div>
          <p className="text-sm text-fg-muted">
            {tr("L'invito è per")} <b className="text-fg">{anteprima.email_destinatario}</b>, che ha
            già un account FantaSquad
            {anteprima.nome_squadra_destinazione && (
              <>
                {" "}
                ({anteprima.tipo_invito === "cessione_squadra" ? tr("ti cedono") : "squadra"}{" "}
                <b className="text-fg">{anteprima.nome_squadra_destinazione}</b>)
              </>
            )}
            . Accedi e l'invito viene accettato subito.
          </p>
          <Link to="/login" state={{ from: destinazione }} className="btn-primary w-full">
            {tr("Accedi e accetta")}
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(tr("La password deve avere almeno 8 caratteri"));
      return;
    }
    setLoading(true);
    try {
      const maglie: Record<string, string | null> = conSquadra ? magliaPayload(maglia) : {};
      if (modo === "invito") {
        if (!urlToken && !codice.trim()) {
          setError(tr("Specifica un codice di invito (o usa il link ricevuto via email)"));
          return;
        }
        const payload: Record<string, string | null> = { password, nome_completo: nome, ...maglie };
        if (urlToken) payload.token = urlToken;
        else payload.codice_leggibile = codice.trim().toUpperCase();
        if (conSquadra && nomeSquadra.trim()) payload.nome_squadra = nomeSquadra.trim();
        const res = await api.post<TokenResponse>("/auth/register", payload);
        setTokens(res.data.access_token, res.data.refresh_token);
        const me = await api.get<User>("/auth/me");
        setUser(me.data);
        // Il secondo atterra sulla gestione della squadra che l'ha invitato.
        setSuccess({ to: isSecondo ? "/rosa" : "/" });
        return;
      }
      const payload: Record<string, string | boolean | null> = {
        email: email.trim(),
        password,
        nome_completo: nome,
        ...maglie,
      };
      if (modo === "crea") payload.per_creare_lega = true;
      if (modo === "lega") {
        if (!codiceLega.trim()) {
          setError(tr("Inserisci il codice della lega"));
          return;
        }
        payload.codice_lega = codiceLega.trim().toUpperCase();
        if (nomeSquadra.trim()) payload.nome_squadra = nomeSquadra.trim();
      }
      const res = await api.post<RegistrazioneResponse>("/auth/registrati", payload);
      setTokens(res.data.access_token, res.data.refresh_token);
      const me = await api.get<User>("/auth/me");
      setUser(me.data);
      setSuccess({ to: modo === "crea" ? "/leghe?crea=1" : res.data.lega_id ? "/" : "/leghe" });
    } catch (err) {
      setError(messaggioErrore(err, tr("Errore durante la registrazione")));
    } finally {
      setLoading(false);
    }
  };

  const sottotitolo =
    modo === null
      ? tr("Come vuoi iniziare?")
      : modo === "lega"
        ? tr(
            "Hai il codice di una lega? Crea il tuo account ed entri subito con la prima squadra libera.",
          )
        : modo === "crea"
          ? tr("Crea il tuo account: subito dopo scegli nome, squadre e modalità della tua lega.")
          : isSecondo
            ? tr("Stai per unirti allo staff di una squadra. Imposta nome e password.")
            : urlToken
              ? tr("Stai accettando un invito. Imposta nome e password per iniziare.")
              : tr("Hai ricevuto un codice di invito? Inseriscilo qui sotto.");

  return (
    <div className="min-h-screen bg-bg text-fg flex items-center justify-center p-6">
      <div className="bg-bg-card border border-primary/30 rounded-card shadow-hero p-8 max-w-md w-full">
        <img
          src="/icon-white-192.png"
          alt="FantaSquad"
          className="w-20 h-20 mx-auto mb-4"
          onError={(e) => {
            e.currentTarget.src = "/icon-192.png";
          }}
        />
        <h1 className="font-display text-3xl text-accent text-center mb-2 tracking-wider">
          {tr("Benvenuto in FantaSquad")}
        </h1>
        <p className="text-fg-muted text-center text-sm mb-6">{sottotitolo}</p>

        {modo === null ? (
          <div className="space-y-3">
            {SCELTE.filter(
              (c) =>
                c.modo === "invito" ||
                (c.modo === "lega" && info?.con_codice_lega) ||
                (c.modo === "crea" && info?.crea_lega),
            ).map((c) => (
              <button
                key={c.modo}
                type="button"
                onClick={() => setModo(c.modo)}
                className="w-full text-left flex items-center gap-3 p-4 rounded-card border border-primary/40 bg-primary-deep/40 hover:border-accent/60 hover:bg-primary-deep/70 transition"
              >
                <span className="text-3xl" aria-hidden>
                  {c.icona}
                </span>
                <span className="min-w-0">
                  <span className="block font-bold text-fg">{c.titolo}</span>
                  <span className="block text-xs text-fg-muted">{c.testo}</span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <>
            {!urlToken && (
              <div className="flex gap-1 mb-4 p-1 rounded-lg bg-primary-deep/60 text-xs">
                {SCELTE.filter(
                  (c) =>
                    c.modo === "invito" ||
                    (c.modo === "lega" && info?.con_codice_lega) ||
                    (c.modo === "crea" && info?.crea_lega),
                ).map((c) => (
                  <button
                    key={c.modo}
                    type="button"
                    onClick={() => {
                      setModo(c.modo);
                      setError(null);
                    }}
                    className={`flex-1 px-2 py-1.5 rounded-md font-bold transition ${
                      modo === c.modo ? "bg-accent text-ink" : "text-fg-muted hover:text-fg"
                    }`}
                  >
                    {c.titolo
                      .replace("Unisciti con un codice", tr("Codice lega"))
                      .replace("Ho un invito via email", tr("Invito email"))}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-4">
              {modo === "invito" && !urlToken && (
                <div>
                  <label className={LABEL}>{tr("Codice invito (XXX-YYY-ZZZ)")}</label>
                  <input
                    type="text"
                    value={codice}
                    onChange={(e) => setCodice(e.target.value.toUpperCase())}
                    required
                    placeholder="ABC-123-XYZ"
                    maxLength={11}
                    className={`${INPUT} font-mono tracking-wider text-center`}
                  />
                </div>
              )}
              {modo === "invito" && urlToken && !isSecondo && (
                <div className="bg-success/10 border border-success/30 rounded p-3 text-sm">
                  {tr("✓ Invito riconosciuto dal link.")}
                </div>
              )}
              {isSecondo && (
                <div className="bg-accent/10 border border-accent/40 rounded p-3 text-sm">
                  {tr("🤝 Entrerai come")} <b>{tr("allenatore in seconda")}</b>
                  {anteprima?.nome_squadra_destinazione && (
                    <>
                      {" "}
                      di <b>{anteprima.nome_squadra_destinazione}</b>
                    </>
                  )}
                  : gestirai formazione, rosa e mercato insieme al presidente. Nessuna squadra nuova
                  da creare.
                </div>
              )}
              {modo === "lega" && (
                <div>
                  <label className={LABEL}>{tr("Codice della lega (XXXX-XXXX)")}</label>
                  <input
                    type="text"
                    value={codiceLega}
                    onChange={(e) => setCodiceLega(e.target.value.toUpperCase())}
                    required
                    placeholder="ABCD-EFGH"
                    maxLength={20}
                    className={`${INPUT} font-mono tracking-widest text-center`}
                  />
                </div>
              )}
              {modo !== "invito" && (
                <div>
                  <label className={LABEL}>{tr("Email")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className={INPUT}
                  />
                </div>
              )}
              <div>
                <label className={LABEL}>{tr("Nome completo")}</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  minLength={2}
                  className={INPUT}
                />
              </div>
              <div>
                <label className={LABEL}>{tr("Password (min 8 caratteri)")}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={INPUT}
                />
              </div>
              {conSquadra && (
                <>
                  <div>
                    <label className={LABEL}>
                      {tr("Nome della tua squadra")}{" "}
                      <span className="opacity-60">{tr("(opzionale)")}</span>
                    </label>
                    <input
                      type="text"
                      value={nomeSquadra}
                      onChange={(e) => setNomeSquadra(e.target.value)}
                      maxLength={120}
                      placeholder={tr("es. Sportlandia FC, La Banda del Pallone, …")}
                      className={INPUT}
                    />
                    <p className="text-xs text-fg-muted mt-1">
                      {tr(
                        tr(
                          "Lasciato vuoto, manterrai il nome provvisorio. Potrai rinominare gratuitamente la 1ª volta dalla pagina Rosa. Cambi successivi: penale 25 fantamilioni.",
                        ),
                      )}
                    </p>
                  </div>

                  <div className="border-t border-primary/20 pt-4">
                    <label className="block text-xs uppercase tracking-wider text-fg-muted mb-3">
                      {tr("La tua maglia")}{" "}
                      <span className="opacity-60">{tr("(potrai cambiarla dopo)")}</span>
                    </label>
                    <MagliaEditor value={maglia} onChange={setMaglia} />
                  </div>
                </>
              )}

              {error && (
                <div className="text-danger text-sm bg-danger/10 border border-danger/30 rounded p-3">
                  ⚠️ {error}
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading
                  ? tr("Registrazione…")
                  : modo === "crea"
                    ? tr("Crea account e vai alla lega")
                    : tr("Crea account")}
              </button>
            </form>
          </>
        )}

        <div className="text-xs text-fg-muted text-center mt-5 space-y-2">
          {modo !== null && !urlToken && (
            <p>
              <button
                type="button"
                className="hover:text-accent underline"
                onClick={() => {
                  setModo(null);
                  setError(null);
                }}
              >
                {tr("← Torna alla scelta")}
              </button>
            </p>
          )}
          <p>
            Hai già un account?{" "}
            <Link to="/login" className="hover:text-accent">
              {tr("Accedi")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
